import {
    defineComponent,
    ref,
    computed,
    onMounted,
    nextTick,
    watch,
    reactive,
} from "/Scripts/vue.esm-browser.js";
import _ from "/Scripts/lodash.js";
import { format, addDays, addMonths, getDay } from "/Scripts/date-fns@4.1.0.js";
import {
    loadDeptCityList,
    loadSearchGroupList,
} from "/Services/Shop/searchEngineBaseService.js";

const html = String.raw;

export default defineComponent({
    name: "SearchEngineM",
    props: {
        webDesignUse: { type: String, default: "" },
        areaCode: { type: String, default: "" },
        startDate: { type: String, default: "" },
        endDate: { type: String, default: "" },
        airPt: { type: String, default: "" },
        signUp: { type: String, default: "0" },
        guarantee: { type: String, default: "0" },
    },
    setup(props) {
        const deptModalOpen = ref(false);
        const destModalOpen = ref(false);
        const showDest = ref("");
        // 基本資料狀態
        const baseDept = ref([]);
        const baseGroup = ref([]);
        const glsDataDept = ref([]);
        const glsDataGroup = ref([]);
        const nowGUID = ref("");

        // 搜尋條件
        const dept = ref("ALL");
        const allSearch = ref([]);
        const dateStart = ref("");
        const dateEnd = ref("");
        const status = ref([]);

        // 計算屬性
        const searchData = computed(() =>
            glsDataGroup.value.length > 0
                ? glsDataGroup.value.find((v) => v.guid === nowGUID.value) || {}
                : {}
        );

        // 目前所選目的地
        const searchGroup = computed(() => {
            if (
                !Array.isArray(allSearch.value) ||
                !Array.isArray(baseGroup.value)
            ) {
                return [];
            }

            const result = allSearch.value
                .map((guid) => {
                    const found = baseGroup.value.find((v) => v.guid === guid);
                    if (!found) {
                        console.warn(`找不到 guid: ${guid} 的對應項目`);
                    }
                    return found;
                })
                .filter(Boolean);

            return result;
        });

        // 日期處理相關函數
        const dateUtils = {
            formatDate: (date) => format(date, "yyyy/MM/dd"),

            getChineseWeekday: (dateString) => {
                if (!dateString) return "";
                const weekdays = ["日", "一", "二", "三", "四", "五", "六"];
                return `(${weekdays[getDay(new Date(dateString))]})`;
            },

            initialDate: () => {
                const today = new Date();
                const tomorrow = addDays(today, 1);
                const twelveMonthsLater = addMonths(tomorrow, 12);

                dateStart.value = dateUtils.formatDate(tomorrow);
                dateEnd.value = dateUtils.formatDate(twelveMonthsLater);
            },
        };

        // 本地儲存管理
        const storageManager = {
            clearSearchEngineRecords: () => {
                const keys = _.filter(
                    _.range(localStorage.length).map((i) =>
                        localStorage.key(i)
                    ),
                    (key) => key?.startsWith("BWTSearchEngine")
                );
                _.forEach(keys, (key) => localStorage.removeItem(key));
            },

            getSearchEngineSettings: (key) => {
                if (!key) return null;
                try {
                    const data = localStorage.getItem(key);
                    return data ? JSON.parse(data) : null;
                } catch (e) {
                    console.error("解析本地儲存資料失敗", e);
                    return null;
                }
            },

            saveSearchEngineSettings: (settings) => {
                const now = new Date();
                const timestamp = format(now, "yyyyMMddHHmmss");

                const key = "BWTSearchEngine" + timestamp;
                localStorage.setItem(key, JSON.stringify(settings));
                return key;
            },
        };

        // 資料處理函數
        const dataProcessor = {
            resetDept: () => {
                const filteredDept = _.filter(
                    baseDept.value,
                    (v) => v.type === "Group"
                );
                glsDataDept.value = filteredDept;
            },

            resetGroup: () => {
                const level1Items = _.filter(
                    baseGroup.value,
                    (v) => v.level === 1
                );

                _.forEach(level1Items, (item) => {
                    item.data = _.filter(
                        baseGroup.value,
                        (v) => v.parentGuid === item.guid
                    );
                });

                _.forEach(
                    _.flatMap(level1Items, (item) => item.data),
                    (item) => {
                        item.data = _.filter(
                            baseGroup.value,
                            (v) => v.parentGuid === item.guid
                        );
                    }
                );

                glsDataGroup.value = level1Items;
            },

            getChildrenByGUID: (guid, level) => {
                if (!guid) return [];

                if (level === 1) {
                    return _.filter(
                        baseGroup.value,
                        (v) => v.parentGuid === guid
                    );
                }

                if (level === 2) {
                    return _.flatMap(
                        _.filter(baseGroup.value, (v) => v.parentGuid === guid),
                        (item) =>
                            _.filter(
                                baseGroup.value,
                                (v) => v.parentGuid === item.guid
                            )
                    );
                }

                return [];
            },

            areAllChildrenSelected: (guid, isSecondLevel = false) => {
                if (!guid) return false;

                const childItems = isSecondLevel
                    ? _.filter(baseGroup.value, (v) => v.parentGuid === guid)
                    : dataProcessor.getChildrenByGUID(guid, 2);

                return (
                    childItems.length > 0 &&
                    _.every(childItems, (item) =>
                        _.includes(allSearch.value, item.guid)
                    )
                );
            },
        };

        // UI狀態處理
        const uiStates = {
            getLiClass: (guid) => {
                if (!guid) return "option";

                const classes = ["option"];

                if (nowGUID.value === guid) {
                    classes.push("selected");
                }

                if (
                    _.some(dataProcessor.getChildrenByGUID(guid, 2), (item) =>
                        _.includes(allSearch.value, item.guid)
                    )
                ) {
                    classes.push("had");
                }

                return classes.join(" ");
            },

            getCheckboxClass: (guid) =>
                !guid
                    ? "checkbox"
                    : allSearch.value.includes(guid)
                        ? "checkbox checked"
                        : "checkbox",

            getCheckboxAllClass: (guid) =>
                !guid
                    ? "all"
                    : dataProcessor.areAllChildrenSelected(guid, true)
                        ? "all checked"
                        : "all",

            getCheckboxAllAreaClass: (guid) =>
                !guid
                    ? "all_area"
                    : dataProcessor.areAllChildrenSelected(guid)
                        ? "all_area checked"
                        : "all_area",

            getStatusClass: (strStatus) => {
                if (!strStatus) return "";

                const baseClass =
                    strStatus === "isSignUp"
                        ? "inputSignUp inputcheckbox checkedbox"
                        : strStatus === "isGuarantee"
                            ? "inputGuarantee6 inputcheckbox checkedbox"
                            : "";

                return status.value.includes(strStatus)
                    ? `${baseClass} checked`
                    : baseClass;
            },
        };

        const getSavedSettings = () => {
            const searchEngineLSKey = window.SearchEngineLSKey || "";
            return storageManager.getSearchEngineSettings(searchEngineLSKey);
        };

        const syncShowDest = () => {
            showDest.value = searchGroup.value
                .map((obj) => obj.areaName)
                .join("/");
        };

        const applySavedDeptSettings = () => {
            const savedSettings = getSavedSettings();
            if (savedSettings?.GroupCity) {
                dept.value = savedSettings.GroupCity;
            }
        };

        const applySavedGroupSettings = () => {
            const savedSettings = getSavedSettings();
            if (!savedSettings?.GroupValue) {
                return;
            }

            _.forEach(savedSettings.GroupValue, (obj) => {
                const matchedItems = _.filter(
                    baseGroup.value,
                    (item) => item.areaCd === obj.area
                );

                if (matchedItems.length > 0) {
                    userInteractions.clickCheckBox(
                        false,
                        false,
                        true,
                        obj.area,
                        matchedItems[0].guid
                    );
                }
            });
        };

        const applyPropDefaults = () => {
            if (props.areaCode) {
                const areaCodes = props.areaCode
                    .split("|")
                    .filter((code) => !!code);

                areaCodes.forEach((areaCD) => {
                    const match = baseGroup.value.find(
                        (item) => item.areaCd === areaCD
                    );
                    if (match && !allSearch.value.includes(match.guid)) {
                        allSearch.value.push(match.guid);
                    }
                });
            }

            if (props.airPt) {
                dept.value = props.airPt;
            }
            if (props.signUp === "1") {
                status.value.push("isSignUp");
            }
            if (props.guarantee === "1") {
                status.value.push("isGuarantee");
            }

            syncShowDest();

            if (glsDataGroup.value.length > 0) {
                userInteractions.clickArea(glsDataGroup.value[0].guid);
            }
        };

        const initializeBaseData = async () => {
            try {
                baseDept.value = _.cloneDeep(await loadDeptCityList());
                dataProcessor.resetDept();
                applySavedDeptSettings();

                baseGroup.value = _.cloneDeep(
                    await loadSearchGroupList(props.webDesignUse)
                );
                if (baseGroup.value.length > 0) {
                    nowGUID.value = baseGroup.value[0].guid;
                }

                dataProcessor.resetGroup();
                applySavedGroupSettings();

                await nextTick();
                applyPropDefaults();
            } catch (error) {
                if (!error?.isHandled && typeof window.ShowDialog === "function") {
                    window.ShowDialog("error", "", error?.message || "搜尋資料載入失敗");
                }
                console.error("SearchEngineM base data init failed:", error);
            }
        };

        // 用戶互動函數
        const userInteractions = {
            openPopup: (event) => {
                document
                    .querySelector(".searchbar_popup")
                    .classList.add("active");
                document.querySelector(".S_sight").classList.add("active");
                event.stopPropagation();
            },

            closePopup: (event) => {
                document
                    .querySelector(".searchbar_popup")
                    .classList.remove("active");
                document.querySelector(".S_sight").classList.remove("active");
                if (event) event.stopPropagation();
            },

            clickArea: (guid, event) => {
                nowGUID.value = guid;
                if (event) event.stopPropagation();
            },

            clickCheckBox: (
                isLevelOne,
                isLevelTwo,
                isLevelThree,
                areaCD,
                guid,
                event
            ) => {
                if (!guid) return;

                if (isLevelOne) {
                    userInteractions.handleFirstLevelCheckbox(guid);
                } else if (isLevelTwo) {
                    userInteractions.handleSecondLevelCheckbox(guid);
                } else if (isLevelThree) {
                    userInteractions.handleThirdLevelCheckbox(guid);
                }

                if (event) event.stopPropagation();
            },

            handleFirstLevelCheckbox: (guid) => {
                const allChildren = dataProcessor.getChildrenByGUID(guid, 2);
                const shouldSelect =
                    !dataProcessor.areAllChildrenSelected(guid);

                if (shouldSelect) {
                    _.forEach(allChildren, (item) => {
                        if (!_.includes(allSearch.value, item.guid)) {
                            allSearch.value.push(item.guid);
                        }
                    });
                } else {
                    allSearch.value = _.filter(
                        allSearch.value,
                        (itemGuid) =>
                            !_.some(
                                allChildren,
                                (child) => child.guid === itemGuid
                            )
                    );
                }
            },

            handleSecondLevelCheckbox: (guid) => {
                const children = _.filter(
                    baseGroup.value,
                    (v) => v.parentGuid === guid
                );
                const shouldSelect = !dataProcessor.areAllChildrenSelected(
                    guid,
                    true
                );

                if (shouldSelect) {
                    _.forEach(children, (item) => {
                        if (!_.includes(allSearch.value, item.guid)) {
                            allSearch.value.push(item.guid);
                        }
                    });
                } else {
                    allSearch.value = _.filter(
                        allSearch.value,
                        (itemGuid) =>
                            !_.some(
                                children,
                                (child) => child.guid === itemGuid
                            )
                    );
                }
            },

            handleThirdLevelCheckbox: (guid) => {
                const isSelected = _.includes(allSearch.value, guid);

                if (isSelected) {
                    allSearch.value = _.filter(
                        allSearch.value,
                        (item) => item !== guid
                    );
                } else {
                    allSearch.value.push(guid);
                }
            },

            clickStatus: (strStatus) => {
                if (!_.includes(status.value, strStatus)) {
                    status.value.push(strStatus);
                } else {
                    status.value = _.filter(
                        status.value,
                        (item) => item !== strStatus
                    );
                }
            },

            clearArea: (guid) => {
                if (!guid) return;

                const childrenToRemove = _.map(
                    dataProcessor.getChildrenByGUID(guid, 2),
                    (item) => item.guid
                );
                allSearch.value = _.filter(
                    allSearch.value,
                    (item) => !_.includes(childrenToRemove, item)
                );
            },

            clearAll: (event) => {
                allSearch.value = [];
                if (event) event.stopPropagation();
            },

            bindDatePicker: () => {
                window.flatpickr("#dateRangePickerM", {
                    mode: "range",
                    dateFormat: "Y-m-d",
                    showMonths: 1,
                    defaultDate: [dateStart.value, dateEnd.value],
                    minDate: "today",
                    locale: {
                        weekdays: {
                            shorthand: [
                                "日",
                                "一",
                                "二",
                                "三",
                                "四",
                                "五",
                                "六",
                            ],
                            longhand: [
                                "星期日",
                                "星期一",
                                "星期二",
                                "星期三",
                                "星期四",
                                "星期五",
                                "星期六",
                            ],
                        },
                        months: {
                            shorthand: [
                                "1月",
                                "2月",
                                "3月",
                                "4月",
                                "5月",
                                "6月",
                                "7月",
                                "8月",
                                "9月",
                                "10月",
                                "11月",
                                "12月",
                            ],
                            longhand: [
                                "1月",
                                "2月",
                                "3月",
                                "4月",
                                "5月",
                                "6月",
                                "7月",
                                "8月",
                                "9月",
                                "10月",
                                "11月",
                                "12月",
                            ],
                        },
                        rangeSeparator: " ~ ",
                        weekAbbreviation: "週",
                        scrollTitle: "滾動切換",
                        toggleTitle: "點擊切換 12/24 小時時制",
                    },
                    onClose: (selectedDates) => {
                        if (selectedDates.length > 0) {
                            dateStart.value = dateUtils.formatDate(
                                selectedDates[0]
                            );
                            dateEnd.value = dateUtils.formatDate(
                                selectedDates[1] || selectedDates[0]
                            );
                        }
                    },
                });
            },
            submit: () => {
                syncShowDest();
                destModalOpen.value = false;
            },
            search: () => {
                storageManager.clearSearchEngineRecords();

                const searchSettings = {
                    GroupCity: dept.value,
                    GroupValue: _.map(searchGroup.value, (v) => ({
                        ul: "ul" + v.parentGuid.toLowerCase(),
                        area: v.areaCd,
                    })),
                    GroupdateSD: dateStart.value,
                    GroupdateED: dateEnd.value,
                    GroupCondition: _.map(status.value, (status) =>
                        status === "isSignUp"
                            ? "inputSignUp"
                            : status === "isGuarantee"
                                ? "inputGuarantee6"
                                : status
                    ),
                };

                const strSEKey =
                    storageManager.saveSearchEngineSettings(searchSettings);
                const searchParams = new URLSearchParams();

                const groupValue = _.map(
                    searchGroup.value,
                    (obj) => obj.areaCd
                ).join("|");
                if (groupValue) searchParams.append("AreaCode", groupValue);

                if (dept.value !== "ALL") {
                    searchParams.append("DeptCity", dept.value);
                }

                if (status.value.length > 0) {
                    searchParams.append("Status", status.value.join("|"));
                }

                searchParams.append("StartDate", dateStart.value);
                searchParams.append("EndDate", dateEnd.value);
                searchParams.append("SEKey", strSEKey);

                //如果出發地都沒選就不要讓他去搜尋結果頁
                //if (searchGroup.value.length == 0) {
                //    document.querySelector(".placeinput").classList.add("error");
                //}
                //else {
                //    window.top.location.href = `/Search?${searchParams.toString()}`;
                //}
                window.top.location.href = `/Search?${searchParams.toString()}`;
            },
        };

        // 監聽選擇變化
        watch(
            () => allSearch.value,
            () => { },
            { deep: true }
        );
        watch(
            () => searchGroup.value,
            (newVal) => {
                if (newVal.length > 0) {
                    document.querySelector(".placeinput").classList.remove("error");
                }
            },
            { deep: true }
        );

        // 初始化
        onMounted(() => {
            dateUtils.initialDate();
            void initializeBaseData();
            if (props.startDate && props.endDate) {
                dateStart.value = props.startDate;
                dateEnd.value = props.endDate;
            }
            userInteractions.bindDatePicker();

            document.addEventListener("click", (event) => {
                const popup = document.querySelector(".searchbar_popup");
                const sight = document.querySelector(".S_sight");

                if (
                    popup?.classList.contains("active") &&
                    !popup.contains(event.target) &&
                    !sight.contains(event.target)
                ) {
                    userInteractions.closePopup(event);
                }
            });
        });

        return {
            dept,
            dateStart,
            dateEnd,
            status,
            searchData,
            searchGroup,
            glsDataDept,
            glsDataGroup,
            nowGUID,
            getChineseWeekday: dateUtils.getChineseWeekday,
            getLiClass: uiStates.getLiClass,
            getCheckboxClass: uiStates.getCheckboxClass,
            getCheckboxAllClass: uiStates.getCheckboxAllClass,
            getCheckboxAllAreaClass: uiStates.getCheckboxAllAreaClass,
            getStatusClass: uiStates.getStatusClass,
            openPopup: userInteractions.openPopup,
            closePopup: userInteractions.closePopup,
            clickArea: userInteractions.clickArea,
            clickCheckBox: userInteractions.clickCheckBox,
            clickStatus: userInteractions.clickStatus,
            clearArea: userInteractions.clearArea,
            clearAll: userInteractions.clearAll,
            search: userInteractions.search,
            submit: userInteractions.submit,
            deptModalOpen,
            destModalOpen,
            showDest,

        };
    },
    template: html`
        <div id="searchEngineM">
            <div class="searchbar_m">
                <div class="searchbar_input">
                    <span @click="deptModalOpen=true;">搜尋</span>
                </div>
            </div>
            <div
                :class="['searchbar_modal-fs', 'modal_a', { active: deptModalOpen }]"
            >
                <div class="drawer_wrapper">
                    <div class="drawer_header">
                        <div class="header_title">搜尋</div>
                        <div
                            class="close_btn"
                            @click="deptModalOpen=false;"
                        ></div>
                    </div>
                    <div class="drawer_body">
                        <div class="input-group">
                            <div class="h1">出發地</div>
                            <select id="selGroupCity" olddept="ALL" v-model="dept">
                                <option
                                    :value="oDept.deptCity"
                                    v-for="oDept in glsDataDept"
                                >
                                    {{oDept.deptName}}
                                </option>
                            </select>
                        </div>
                        <div class="input-group placeinput">
                            <div class="h1">目的地</div>
                            <div
                                class="input"
                                data-text="請選擇目的"
                                v-if="showDest"
                                @click="destModalOpen=true;"
                                v-html="showDest"
                            >
                            </div>
                            <div
                                class="input"
                                data-text="請選擇目的"
                                v-else
                                @click="destModalOpen=true;"
                            >
                                請選擇目的
                            </div>
                        </div>
                        <div class="input-group">
                            <div class="h1">出發日期</div>
                            <div class="input">
                                <div class="dep_date" id="dateRangePickerM">
                                    <span>
                                        {{dateStart}}{{getChineseWeekday(dateStart)}}
                                    </span>
                                    <span>~</span>
                                    <span>
                                        {{dateEnd}}{{getChineseWeekday(dateEnd)}}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="filterstatus">
                            <ul>
                                <li
                                    id="inputSignUp"
                                    :class="getStatusClass('isSignUp')"
                                    @click="clickStatus('isSignUp')"
                                >
                                    可報名
                                </li>
                                <li
                                    id="inputGuarantee6"
                                    :class="getStatusClass('isGuarantee')"
                                    @click="clickStatus('isGuarantee')"
                                >
                                    保證出發
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div class="drawer_footer">
                        <button
                            class="search_btn_m M_btn-b"
                            @click="search()"
                        >
                            搜尋
                        </button>
                    </div>
                </div>
            </div>
            <div
                :class="['searchDEST_modal Com_PopupBg', { active: destModalOpen }]"
            >
                <div class="sign_search sign_search_m">
                    <div
                        class="searchbar_popup_m pb_popup_a popup-active Com_bottomPopup"
                    >
                        <div class="drawer_header">
                            <div class="h1">選擇目的地</div>
                            <div
                                class="close_btn"
                                @click="destModalOpen=false;"
                            ></div>
                        </div>
                        <div class="drawer_body">
                            <div class="regions dragscroll">
                                <ul class="">
                                    <li
                                        v-for="oArea in glsDataGroup"
                                        :class="getLiClass(oArea.guid)"
                                        :area="oArea.areaCd"
                                        :guid="oArea.guid"
                                        @click="clickArea(oArea.guid)"
                                    >
                                        <span v-html="oArea.areaName"></span>
                                    </li>
                                </ul>
                            </div>
                            <div class="area">
                                <ul>
                                    <li
                                        :class="getCheckboxAllAreaClass(searchData.guid)"
                                        :area="searchData.areaCd"
                                        :guid="searchData.guid"
                                        @click="clickCheckBox(true,false,false,searchData.areaCd,searchData.guid)"
                                    >
                                        全區選擇
                                    </li>
                                </ul>
                                <ul
                                    class="area-item"
                                    v-for="oData in searchData.data"
                                >
                                    <li
                                        :class="getCheckboxAllClass(oData.guid)"
                                        :area="oData.areaCd"
                                        :guid="oData.guid"
                                        v-html="oData.areaName"
                                        @click="clickCheckBox(false,true,false,oData.areaCd,oData.guid)"
                                    ></li>
                                    <li
                                        :class="getCheckboxClass(oDetail.guid)"
                                        :area="oDetail.areaCd"
                                        :guid="oDetail.guid"
                                        v-for="oDetail in oData.data"
                                        @click="clickCheckBox(false,false,true,oDetail.areaCd,oDetail.guid)"
                                    >
                                        {{oDetail.areaName}}
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div class="row">
                            <button
                                class="tag_delete"
                                @click="clearArea(searchData.guid)"
                            >
                                清除選項
                            </button>
                            <button class="submit_btn M_btn-b" @click="submit">送出</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `,
});
